/**
 * printScoresheet.js — printable/archival game scoresheet
 * ─────────────────────────────────────────────────────────────────
 * League gap-closure Phase G (2026-06-09). Plan §5c asked for a
 * "generated scoresheet PDF"; this delivers the same artifact with
 * zero new dependencies: a print-ready HTML document opened in a new
 * window with window.print(). The browser's "Save as PDF" produces
 * the archival file; a real printer produces the rink copy.
 *
 * Data comes from what LeagueGameSheetPage already loads (sheets /
 * lineup / coaches / goals / penalties) plus the officials list.
 * Signature images are the stored base64 PNGs — they only appear for
 * viewers the API gave them to (coaches/managers), so the print
 * mirrors on-screen authorization exactly.
 */

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;')
  .replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const PERIODS = { 1: '1st', 2: '2nd', 3: '3rd', 4: 'OT', 5: 'SO' };

function rosterTable(rows) {
  if (!rows || rows.length === 0) return '<p class="muted">No lineup recorded.</p>';
  const sorted = rows.slice().sort((a, b) => (a.jersey_number ?? 999) - (b.jersey_number ?? 999));
  return `
    <table>
      <thead><tr><th class="num">#</th><th>Player</th><th>Pos</th><th></th></tr></thead>
      <tbody>
        ${sorted.map(r => `
          <tr>
            <td class="num">${esc(r.jersey_number)}</td>
            <td>${esc(r.display_name || r.ad_hoc_name || '(unnamed)')}${r.ad_hoc_name && !r.display_name ? ' <span class="muted">(sub)</span>' : ''}</td>
            <td>${esc((r.position || '').slice(0, 1).toUpperCase())}</td>
            <td>${r.is_captain ? 'C' : r.is_alternate_captain ? 'A' : ''}${r.is_starting ? ' ★' : ''}</td>
          </tr>`).join('')}
      </tbody>
    </table>`;
}

function coachesList(rows) {
  if (!rows || rows.length === 0) return '<p class="muted">—</p>';
  return `<p>${rows.map(c => `${esc(c.coach_name)} <span class="muted">(${esc((c.coach_position || '').replace('_', ' '))})</span>`).join(' · ')}</p>`;
}

function signatureBlock(title, name, when, b64) {
  return `
    <div class="sig">
      <div class="sig-title">${esc(title)}</div>
      ${b64 ? `<img src="${b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`}" alt="signature" />` : '<div class="sig-line"></div>'}
      <div class="sig-name">${esc(name || '')}${when ? ` · ${new Date(when).toLocaleString()}` : ''}</div>
    </div>`;
}

export function printScoresheet({
  leagueName, game, homeSheet, awaySheet, lineup, coaches,
  goals, penalties, officials, homeScore, awayScore,
}) {
  const teamName = (id) => id === game.home_team_id ? game.home_team_name : game.away_team_name;
  const dateStr = game.game_date ? String(game.game_date).slice(0, 10) : '';
  const timeStr = game.game_time ? String(game.game_time).slice(0, 5) : '';

  const goalsRows = (goals || []).map(g => `
    <tr>
      <td>${PERIODS[g.period] || g.period}</td>
      <td>${esc(g.time_in_period || '—')}</td>
      <td>${esc(teamName(g.team_id) || '')}</td>
      <td>${esc(g.scorer_name)}${g.scorer_is_ad_hoc ? ' <span class="muted">(sub)</span>' : ''}</td>
      <td>${esc(g.assist1_name || '')}${g.assist2_name ? `, ${esc(g.assist2_name)}` : ''}</td>
      <td>${[g.is_power_play && 'PP', g.is_short_handed && 'SH', g.is_empty_net && 'EN', g.is_shootout && 'SO'].filter(Boolean).join(' ')}</td>
    </tr>`).join('');

  const penRows = (penalties || []).map(p => `
    <tr>
      <td>${PERIODS[p.period] || p.period}</td>
      <td>${esc(p.time_in_period || '—')}</td>
      <td>${esc(teamName(p.team_id) || '')}</td>
      <td>${esc(p.player_name)}${p.player_is_ad_hoc ? ' <span class="muted">(sub)</span>' : ''}</td>
      <td>${esc(p.infraction)}</td>
      <td>${esc(p.minutes)} min${p.is_major ? ' · major' : ''}${p.is_misconduct ? ' · misconduct' : ''}</td>
    </tr>`).join('');

  const officialSigs = (officials || []).map(o =>
    signatureBlock(
      (o.role || '').replace('_', ' '),
      o.official_name || o.assigned_name || '',
      o.signed_at,
      o.signature_b64
    )).join('');

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>Scoresheet — ${esc(game.home_team_name)} vs ${esc(game.away_team_name)} — ${esc(dateStr)}</title>
<style>
  * { box-sizing: border-box; }
  body { font: 12px/1.45 -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #111; margin: 24px; }
  h1 { font-size: 18px; margin: 0 0 2px; }
  h2 { font-size: 13px; margin: 18px 0 6px; border-bottom: 1px solid #999; padding-bottom: 2px; text-transform: uppercase; letter-spacing: 0.05em; }
  .muted { color: #666; }
  .header { display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: 8px; }
  .score { font-size: 22px; font-weight: 800; }
  .locked { border: 2px solid #111; display: inline-block; padding: 2px 10px; font-weight: 800; transform: rotate(-2deg); }
  .cols { display: flex; gap: 24px; }
  .col { flex: 1; min-width: 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 4px; }
  th, td { border: 1px solid #bbb; padding: 3px 6px; text-align: left; font-size: 11px; }
  th { background: #eee; }
  .num { width: 32px; text-align: right; }
  .sigs { display: flex; gap: 18px; flex-wrap: wrap; margin-top: 8px; }
  .sig { width: 200px; }
  .sig-title { font-size: 10px; text-transform: uppercase; color: #555; }
  .sig img { max-width: 190px; max-height: 60px; border-bottom: 1px solid #999; }
  .sig-line { height: 40px; border-bottom: 1px solid #999; }
  .sig-name { font-size: 10px; color: #333; }
  .footer { margin-top: 22px; font-size: 9px; color: #888; }
  @media print { body { margin: 10mm; } }
</style>
</head>
<body>
  <div class="header">
    <div>
      <h1>${esc(game.home_team_name)} vs ${esc(game.away_team_name)}</h1>
      <div class="muted">${esc(leagueName || 'League game')} · ${esc(dateStr)}${timeStr ? ` · ${esc(timeStr)}` : ''}${game.venue_name ? ` · ${esc(game.venue_name)}` : ''}</div>
    </div>
    <div style="text-align:right">
      <div class="score">${homeScore} — ${awayScore}</div>
      ${game.is_scoresheet_locked ? '<div class="locked">FINAL</div>' : '<div class="muted">unofficial</div>'}
    </div>
  </div>

  <div class="cols">
    <div class="col">
      <h2>${esc(game.home_team_name)} — lineup</h2>
      ${rosterTable(lineup.home)}
      <h2>Bench staff</h2>
      ${coachesList(coaches.home)}
    </div>
    <div class="col">
      <h2>${esc(game.away_team_name)} — lineup</h2>
      ${rosterTable(lineup.away)}
      <h2>Bench staff</h2>
      ${coachesList(coaches.away)}
    </div>
  </div>

  <h2>Goals</h2>
  ${goalsRows
    ? `<table><thead><tr><th>Per</th><th>Time</th><th>Team</th><th>Scorer</th><th>Assists</th><th>Flags</th></tr></thead><tbody>${goalsRows}</tbody></table>`
    : '<p class="muted">No goals recorded.</p>'}

  <h2>Penalties</h2>
  ${penRows
    ? `<table><thead><tr><th>Per</th><th>Time</th><th>Team</th><th>Player</th><th>Infraction</th><th>Minutes</th></tr></thead><tbody>${penRows}</tbody></table>`
    : '<p class="muted">No penalties recorded.</p>'}

  <h2>Sign-off</h2>
  <div class="sigs">
    ${signatureBlock(`${esc(game.home_team_name)} coach`, homeSheet?.signed_by_name, homeSheet?.signed_at, homeSheet?.signature_b64)}
    ${signatureBlock(`${esc(game.away_team_name)} coach`, awaySheet?.signed_by_name, awaySheet?.signed_at, awaySheet?.signature_b64)}
    ${officialSigs}
  </div>

  <div class="footer">
    Generated by Game Day on ${new Date().toLocaleString()} · game ${esc(game.id)}
  </div>
  <script>window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 150); });</script>
</body>
</html>`;

  const w = window.open('', '_blank', 'noopener,width=900,height=700');
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
